import { IPaginationReturn } from '../../interfaces/IPaginationReturn';
import { useRequest } from '../providers/useRequest';
import AppError from '../../errors/AppError';
import ICostCenter from '../../interfaces/ICostCenter';

export interface IFilterCostCenter {
  id?: number | null;
}

const useCostCenterService = () => {
  const request = useRequest<ICostCenter>();
  const path = 'employee/costCenter';

  const list = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
    filter?: IFilterCostCenter,
  ): Promise<IPaginationReturn<ICostCenter[]>> => {
    return await request
      .postAndGetManyPaginated({
        path: `${path}/list?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
        body: { ...filter },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const findById = async (id: number): Promise<ICostCenter> => {
    return await request
      .getOne({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCostCenterByDescription = async (name: string): Promise<ICostCenter[]> => {
    return await request
      .getMany({
        path: `${path}/search/${name}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCostCenterSearch = async (filter: string): Promise<ICostCenter[]> => {
    if (filter === '') {
      filter = ' ';
    }

    const url = filter
      ? `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter=${filter}`
      : `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter= `;

    return await request
      .getManyPaginated({
        path: url,
        sendAuthorization: true,
      })
      .then(result => {
        return result?.data;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCostCenter = async (): Promise<ICostCenter[]> => {
    return await request.getMany({
      path: path,
      sendAuthorization: true,
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCostCenterByrequestId = async (id: number): Promise<ICostCenter[]> => {
    return await request
      .getMany({
        path: `${path}/search/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const create = async ({ code, name }: ICostCenter): Promise<ICostCenter> => {
    return await request
      .post({
        path: `${path}`,
        sendAuthorization: true,
        body: { code, name },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const update = async (id: number, { code, name }: ICostCenter): Promise<ICostCenter> => {
    return await request
      .put({
        path: `${path}/${id}`,
        sendAuthorization: true,
        body: { code, name },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const remove = async (id: number): Promise<void> => {
    return await request
      .remove({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return {
    list,
    listCostCenterByDescription,
    findById,
    listCostCenterSearch,
    listCostCenter,
    listCostCenterByrequestId,
    create,
    update,
    remove,
  };
};

export { useCostCenterService };
